import { EntityPipelineStageId, PipelineStageId, RegionId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { DraggableCard } from "components/dashboard/column";
import { trpc } from "components/rpc/trpc";
import { Badge } from "components/ui/badge";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { DroppableWrapper } from "util/dnd";
import { EmptyCard } from "../dashboard/card";
import { AddCardToStageButton } from "./add-referral-to-board";
import { ReferralPipelineCard } from "./referral-pipeline-card";
import {
    BoardStageCard,
    PipelineStageSummary,
    ReferralPipelineBoardQuery,
    referralPipelineCardType,
} from "./types";

interface ReferralPipelineColumnProps {
    stage: PipelineStageSummary;
    regionId: RegionId;
    boardQuery: ReferralPipelineBoardQuery;
    dragHandler: (
        membershipId: EntityPipelineStageId,
        fromStageId: PipelineStageId,
        toStageId: PipelineStageId,
    ) => void;
    highlightedCards: string[];
    pendingCards: string[];
    onRemove: (stageId: PipelineStageId, card: BoardStageCard) => void;
}

export const ReferralPipelineColumn: React.FC<ReferralPipelineColumnProps> = props => {
    const {
        stage,
        regionId,
        boardQuery,
        dragHandler,
        highlightedCards,
        pendingCards,
        onRemove,
    } = props;
    const trpcContext = trpc.useTRPC();
    const cardsQuery = useQuery({
        ...trpcContext.pipeline.boardStage.queryOptions({
            stageId: stage.id,
            regionId,
            ...boardQuery,
        }),
    });
    const cards = cardsQuery.data;

    const handleRemove = React.useCallback(
        (card: BoardStageCard) => {
            onRemove(stage.id, card);
        },
        [onRemove, stage.id],
    );

    if (!cards) {
        return (
            <div className="col pipeline-stage">
                <div className="pipeline-innards">
                    <div className="pipeline-header">
                        <div className="pipeline-header-title">{stage.name}</div>
                        <div className="pipeline-header-badge">
                            <Spinner />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="col pipeline-stage">
            <DroppableWrapper accept={referralPipelineCardType} id={stage.id}>
                <div className="pipeline-innards">
                    <div className="pipeline-header">
                        <div className="pipeline-header-title">{stage.name}</div>
                        <div className="pipeline-header-badge">
                            <Badge type="info" value={cards.length} />
                        </div>
                    </div>
                    <div className="pipeline-content">
                        {cards.length > 0 ? (
                            cards.map(card => (
                                <DraggableCard
                                    key={card.id}
                                    id={card.id}
                                    type={referralPipelineCardType}
                                    onDrop={(target: string) =>
                                        dragHandler(
                                            card.id,
                                            stage.id,
                                            target as PipelineStageId,
                                        )
                                    }>
                                    <ReferralPipelineCard
                                        card={card}
                                        highlightCard={highlightedCards.includes(card.id)}
                                        pendingCard={pendingCards.includes(card.id)}
                                        onRemove={handleRemove}
                                    />
                                </DraggableCard>
                            ))
                        ) : (
                            <EmptyCard />
                        )}
                    </div>
                    <div className="px-2 pt-1">
                        <AddCardToStageButton stage={stage} regionId={regionId} />
                    </div>
                </div>
            </DroppableWrapper>
        </div>
    );
};
